import React, { useMemo, useRef, useState, useEffect } from "react";
import StepperStepComponent from "./stepper-step";
import StepperConnector from "./stepper-connector";
import { Box, Stack, useTheme } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { FunnelStageResponse } from "@/modules/lead-page/types";
import { StepState } from "@/modules/common/components/ui-kit/stepper/types";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";

export type StepperStep = {
  id: string;
  label: string;
  icon: React.ReactNode;
  state: StepState;
};

type StepperProps = {
  stepsData?: FunnelStageResponse;
};

const Stepper: React.FC<StepperProps> = ({ stepsData }) => {
  const { isMobile } = useScreenSize();
  const theme = useTheme();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const stepperSteps: StepperStep[] = useMemo(() => {
    const steps = stepsData?.data ?? [];
    return steps.map((step) => ({
      id: step.id.toString(),
      label: step.name,
      state: step.state,
      icon: (
        <img
          src={
            [StepState.CURRENT, StepState.FINISHED].includes(step.state)
              ? step.activeIcon
              : step.inactiveIcon
          }
          alt={step.name}
          style={{ width: "20px", height: "20px" }}
        />
      ),
    }));
  }, [stepsData?.data]);

  const items: React.ReactNode[] = [];
  stepperSteps.forEach((step, idx) => {
    items.push(<StepperStepComponent key={step.id} step={step} />);
    if (idx < stepperSteps.length - 1) {
      items.push(<StepperConnector key={`connector-${step.id}`} />);
    }
  });

  const handleScrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -100, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 100, behavior: "smooth" });
  };

  const updateFadeVisibility = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const tolerance = 1;
    const { scrollLeft, clientWidth, scrollWidth } = container;
    const normalizedScrollLeft = Math.max(scrollLeft, 0);

    setShowLeftFade(normalizedScrollLeft > tolerance);
    setShowRightFade(
      normalizedScrollLeft + clientWidth < scrollWidth - tolerance
    );
  };

  useEffect(() => {
    updateFadeVisibility();
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", updateFadeVisibility);
    window.addEventListener("resize", updateFadeVisibility);

    return () => {
      container.removeEventListener("scroll", updateFadeVisibility);
      window.removeEventListener("resize", updateFadeVisibility);
    };
  }, [stepperSteps]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Left Arrow + Fade */}
      {!isMobile && showLeftFade && (
        <Box
          onClick={handleScrollLeft}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            background: showLeftFade
              ? "linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0) 100%)"
              : "transparent",
            cursor: "pointer",
            zIndex: 2,
          }}
        >
          <ChevronLeft
            sx={{
              width: "20px",
              height: "20px",
              color: theme.nawy.colors.primitives.grey[900],
            }}
          />
        </Box>
      )}

      {/* Stepper Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "visible",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            width: "max-content",
            minHeight: "40px",
          }}
        >
          {items}
        </Stack>
      </Box>

      {/* Right Arrow */}
      {!isMobile && showRightFade && (
        <Box
          onClick={handleScrollRight}
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            background: showRightFade
              ? "linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0) 100%)"
              : "transparent",
            cursor: "pointer",
            zIndex: 2,
          }}
        >
          <ChevronRight
            sx={{
              width: "20px",
              height: "20px",
              color: theme.nawy.colors.primitives.grey[900],
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default Stepper;
